<template>
  <div class="page">
    <div class="page_contents">
      <div class="top_menu">
        <div :class="{ active: tabIdx }" @click="initSearch(true)">진행중인 공고</div>
        <div :class="{ active: !tabIdx }" @click="initSearch(false)">종료된 공고</div>
      </div>
      <div class="contents_div">
        <div class="page_header" v-show="tabIdx">
          <div class="page_header_circle"></div>
          진행중인 공고
        </div>
        <div class="page_header" v-show="!tabIdx">
          <div class="page_header_circle"></div>
          종료된 공고
        </div>
        <table class="styled-table">
          <thead>
            <tr>
              <th>연번</th>
              <th>공고기관</th>
              <th>공고번호</th>
              <th>공고명</th>
              <th>수행구분</th>
              <th>낙찰금액</th>
              <th>수행금액</th>
              <th>수행상태</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(announcement, index) in paginatedAnnouncements" :key="index" @click="goDetail(announcement)">
              <td>{{ announcement.공고순번 }}</td>
              <td class="align_left">{{ announcement.공고기관 }}</td>
              <td class="align_left">{{ announcement.공고번호 }}</td>
              <td class="align_left">{{ announcement.공고명 }}</td>
              <td class="align_left">{{ announcement.수주수행구분 }}</td>
              <td class="align_right">{{ numberFormat(announcement.낙찰금액) }}원</td>
              <td class="align_right">{{ numberFormat(announcement.수행금액) }}원</td>
              <td>{{ announcement.수행상태 }}</td>
            </tr>
          </tbody>
        </table>
        <div class="pagination">
          <img class="pagination_left_arrow" @click="prevPage" src="@/assets/images/paging_arrow_left.png" />
          <button v-for="page in pages" :key="page" @click="goToPage(page)" :class="{ active: page === currentPage }">
            {{ page }}
          </button>
          <img class="pagination_right_arrow" @click="nextPage" src="@/assets/images/paging_arrow_right.png" />
        </div>
      </div>
    </div>
  </div>
  <ContractPop v-if="contractFlag" :data="clickData.계약서" @close="contractClose" :seq="clickData.공고순번" :seq2="seq" :type="'U'"></ContractPop>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import announceAPI from "../api/announceAPI";
import { Announces } from "../common/types";
import { numberFormat } from "../utils/utils";
import ContractPop from "@/components/popup/ContractPop.vue";

const contractFlag = ref(false);

const router = useRouter();
const announcements = ref<Announces[]>([]); // 전체 데이터를 저장
const tabIdx = ref(true); // 탭 인덱스
const currentPage = ref(1); // 현재 페이지 번호
const pageSize = ref(7); // 한 페이지당 보여줄 데이터 개수

const contractClose = () => {
  contractFlag.value = false;
};
// 현재 페이지에 맞는 데이터 계산
const paginatedAnnouncements = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return announcements.value.slice(start, end);
});

// 총 페이지 수 계산
const totalPages = computed(() => Math.ceil(announcements.value.length / pageSize.value));

// 페이지 버튼 계산
const pages = computed(() => {
  const maxPagesToShow = 5; // 보여줄 최대 페이지 버튼 수
  const half = Math.floor(maxPagesToShow / 2);
  let start = Math.max(1, currentPage.value - half);
  let end = Math.min(totalPages.value, currentPage.value + half);

  if (currentPage.value <= half) {
    end = Math.min(totalPages.value, maxPagesToShow);
  } else if (currentPage.value + half >= totalPages.value) {
    start = Math.max(1, totalPages.value - maxPagesToShow + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// 초기 데이터 로드
onMounted(() => {
  getAnnounceList();
});

// 공고 데이터 가져오기
const getAnnounceList = () => {
  announceAPI.announceList({진행여부:tabIdx.value}).then((res) => {
    announcements.value = res.data.data; // API에서 전체 데이터를 가져옴
  });
};

// 검색 초기화
const initSearch = (idx: boolean) => {
  tabIdx.value = idx;
  currentPage.value = 1;
  getAnnounceList();
};
const clickData = ref()
const seq = ref()
// 상세 페이지 이동
const goDetail = (data: Announces) => {
  if(!data.계약서) {
    alert("등록된 계약서가 없습니다.");
    router.push({ name: `announceDetail`, params: { seq: data.공고순번 } });
  }
  if(data.계약서[`${data.수주수행구분}계약서작성여부`]) {
    router.push({ name: `announceDetail`, params: { seq: data.공고순번 } });
  } else {
    alert("계약서 작성이 완료되지 않았습니다.");
    clickData.value = data
    seq.value = data.계약서.저장파일.저장순번
    contractFlag.value = true
  }
};

// 다음 페이지로 이동
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// 이전 페이지로 이동
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// 특정 페이지로 이동
const goToPage = (page: number) => {
  currentPage.value = page;
};
</script>

<style scoped>
/* 스타일 작성 */
.active {
  font-weight: bold;
  color: blue;
}
</style>

<style scoped>
.page {
  min-height: 622px;
}
.page_header {
  border-bottom: 1px solid #e0e0e0;
  padding: 35px 0px 20px 0px;
  font-family: "Noto Sans CJK KR-Bold";
  font-size: 23px;
  display: flex;
  gap: 12px;
  align-items: center;
  line-height: 35px;
}
.page_header_circle {
  width: 18px;
  height: 18px;
  border-radius: 18px;
  border: 5px solid #e5e8ec;
}
.page_contents {
  padding: 40px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.contents_div {
  width: 1100px;
}
.styled-table {
  width: 100%;
  border-collapse: collapse;
  font-family: "Noto Sans KR", sans-serif;
  border-top: 3px solid #041e42;
}
.styled-table th {
  padding: 10px;
  text-align: center;
  font-size: 13px;
  border-right: 1px solid #dbdbdb;
  border-bottom: 1px solid #dbdbdb;
  font-family: "Noto Sans CJK KR-Bold";
}
.styled-table td {
  padding: 10px;
  text-align: center;
  font-size: 13px;
  border-right: 1px solid #dbdbdb;
  border-bottom: 1px solid #dbdbdb;
  font-family: "Noto Sans CJK KR-Regular";
}

.styled-table thead tr {
  background-color: #f6f7fb;
  color: #272727;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  font-family: "Noto Sans CJK KR-Bold";
}
.styled-table tbody tr {
  cursor: pointer;
}
.styled-table tbody tr:hover {
  background-color: #f1f1f1;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 5px;
}

.pagination_left_arrow {
  margin-right: 30px;
  cursor: pointer;
}
.pagination_right_arrow {
  margin-left: 30px;
  cursor: pointer;
}
.pagination button {
  font-weight: bold;
  width: 26px;
  height: 26px;
  border-radius: 26px;
  border: none;
  background: transparent;
  cursor: pointer;
}
.pagination button.active {
  font-weight: bold;
  background-color: black;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 26px;
}
.top_menu {
  display: flex;
  flex-direction: row;
  font-size: 15px;
}
.top_menu div {
  cursor: pointer;
  width: 550px;
  border: 1px solid #dbdbdb;
  height: 61px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-family: "Noto Sans CJK KR-Bold";
  color: #b7b7b7;
}
.top_menu div:hover {
  color: #337ab7;
  font-weight: bold;
  font-family: "Noto Sans CJK KR-Bold";
}
.top_menu div.active {
  color: black;
  font-weight: bold;

  border-bottom: none;
  border-top: 3px solid #041e42;
}

/* 공통으로 뺴야할부분 */
.align_left {
  text-align: left !important;
}
.align_right {
  text-align: right !important;
}

.styled-table th:nth-child(1),
.styled-table tr:nth-child(1) {
  width: 90px;
}

.styled-table th:nth-child(2),
.styled-table tr:nth-child(2) {
  width: 130px;
}
.styled-table th:nth-child(3),
.styled-table tr:nth-child(3) {
  width: 150px;
}
.styled-table th:nth-child(4),
.styled-table tr:nth-child(4) {
  width: 300px;
}
.styled-table td:nth-child(4) {
  font-family: "Noto Sans CJK KR-Bold";
  color: #232a58;
}
</style>
